import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { FetchBaseQueryError } from '@reduxjs/toolkit/src/query';
import { clearAuthAndRedirect } from '@/entities/Auth';
import { LocalStorageService } from '@/shared/services/localStorage.service';
import type { RejectApiResponse } from '@/shared/types/common';
import {
  GET_NOTIFICATIONS,
  GET_NOTIFICATIONS_UNREAD_COUNT,
  GET_DASHBOARD,
  GET_ENROLLMENTS,
  GET_ENROLLMENT_TERMS,
  GET_ENROLLMENT_INTAKES,
  GET_ENROLLMENT_TERM,
  GET_MODULE_LESSONS,
  GET_ENROLLMENT_LESSON,
  GET_ASSIGNMENTS,
  GET_ASSIGNMENT_SUBMISSION,
  GET_PROFILE,
  GET_TEACHER_ASSIGNMENTS,
  GET_TEACHER_ASSIGNMENTS_SUMMARY,
  GET_TEACHER_ASSIGNMENTS_DETAILS,
  GET_TEACHER_ASSIGNMENTS_COURSES,
  GET_TEACHER_LESSON_BASE,
  GET_TEACHER_LESSON_STUDENTS,
  GET_TEACHER_LESSON_SUBMISSION,
  GET_TEACHER_COURSES,
  GET_TEACHER_INTAKES,
  GET_TEACHER_TERMS,
  GET_TEACHER_TERM_MODULES,
  GET_TEACHER_TERM_STUDENTS,
  GET_TEACHER_LESSONS,
  GET_TEACHER_LESSON_DETAILS,
  GET_TEACHER_ATTENDANCE,
  GET_TEACHER_ATTENDANCE_HISTORY,
  GET_TEACHER_MODULE_ATTENDANCE,
  GET_STUDENT_SCORM,
  GET_TEACHER_SCORM,
  GET_STUDENT_REQUESTS,
  GET_TEACHER_REQUESTS,
  GET_CALENDAR,
  GET_CALENDAR_SUBSCRIPTION,
  GET_CATALOGUE_COURSE,
  GET_STUDENT_INVOICES_BALANCE,
  GET_STUDENT_BADGES,
  GET_BLOGS,
  GET_BLOG,
} from './tags';

type ExtraOptions = {
  skipAuthRedirect?: boolean;
};

/**
 * Публичные эндпоинты, которым токен не нужен. Подтверждение удаления по почте: бэкенд
 * принимает его без входа, а токен удаляемой учётки мог бы прийти уже отозванным — 401.
 */
const ANONYMOUS_ENDPOINTS = new Set(['requestAccountDeletionByEmail']);

const baseQuery = fetchBaseQuery({
  baseUrl: __API__,
  credentials: 'include',
  prepareHeaders: (headers, { endpoint }) => {
    const accessToken = LocalStorageService.getAccessTokenFromAnyStorage();
    if (accessToken && !ANONYMOUS_ENDPOINTS.has(endpoint)) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    headers.set('Accept', 'application/json');
    return headers;
  },
});

const readMessage = (source: any): string | null =>
  source &&
  typeof source === 'object' &&
  'message' in source &&
  source.message !== null &&
  source.message !== undefined
    ? String(source.message)
    : null;

const toRejectApiResponse = (error: FetchBaseQueryError): RejectApiResponse => {
  const data = (error as any).data;
  const status = typeof error.status === 'number' ? error.status : undefined;

  if (data && typeof data === 'object') {
    // Живут две формы отказа, обе валидны:
    // 1) наш обработчик — { success, message, details } на верхнем уровне;
    // 2) Strapi — { data: null, error: { status, name, message, details } }: приходит
    //    от отказов на уровне политик, до нашего кода.
    const nested = (data as any).error;
    const source = nested && typeof nested === 'object' ? nested : null;

    // api data
    const message = readMessage(data) ?? readMessage(source);
    const details = (data as any).details ?? source?.details;

    if (message !== null || source) {
      return {
        success: false,
        message: message ?? 'Request failed',
        status,
        type: (data as any).type ?? source?.type ?? source?.name,
        details,
      };
    }
  }

  // if data is string
  if (typeof data === 'string' && data.trim()) {
    return { success: false, message: data, status };
  }

  // fallback
  return { success: false, message: 'Request failed', status };
};

export const baseQueryWithInterceptor: BaseQueryFn<
  string | FetchArgs,
  unknown,
  RejectApiResponse,
  ExtraOptions,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error) {
    const fbqError = result.error as FetchBaseQueryError;
    // Редирект при 401 (Unauthorized), или 403 (Forbidden) если error.message === "Forbidden"file
    if (
      (fbqError.status === 401 ||
        (fbqError.status === 403 && (fbqError as any)?.data?.error?.message === 'Forbidden')) &&
      !extraOptions?.skipAuthRedirect
    ) {
      clearAuthAndRedirect('/login');
    }

    return { error: toRejectApiResponse(fbqError), meta: result.meta };
  }

  return result;
};

export const rtkApi = createApi({
  reducerPath: 'api',
  tagTypes: [
    GET_NOTIFICATIONS,
    GET_NOTIFICATIONS_UNREAD_COUNT,
    GET_DASHBOARD,
    GET_ENROLLMENTS,
    GET_ENROLLMENT_TERMS,
    GET_ENROLLMENT_INTAKES,
    GET_ENROLLMENT_TERM,
    GET_MODULE_LESSONS,
    GET_ENROLLMENT_LESSON,
    GET_ASSIGNMENTS,
    GET_ASSIGNMENT_SUBMISSION,
    GET_PROFILE,
    GET_TEACHER_ASSIGNMENTS,
    GET_TEACHER_ASSIGNMENTS_SUMMARY,
    GET_TEACHER_ASSIGNMENTS_DETAILS,
    GET_TEACHER_ASSIGNMENTS_COURSES,
    GET_TEACHER_LESSON_BASE,
    GET_TEACHER_LESSON_STUDENTS,
    GET_TEACHER_LESSON_SUBMISSION,
    GET_TEACHER_COURSES,
    GET_TEACHER_INTAKES,
    GET_TEACHER_TERMS,
    GET_TEACHER_TERM_MODULES,
    GET_TEACHER_TERM_STUDENTS,
    GET_TEACHER_LESSONS,
    GET_TEACHER_LESSON_DETAILS,
    GET_TEACHER_ATTENDANCE,
    GET_TEACHER_ATTENDANCE_HISTORY,
    GET_TEACHER_MODULE_ATTENDANCE,
    GET_STUDENT_SCORM,
    GET_TEACHER_SCORM,
    GET_STUDENT_REQUESTS,
    GET_TEACHER_REQUESTS,
    GET_CALENDAR,
    GET_CALENDAR_SUBSCRIPTION,
    GET_CATALOGUE_COURSE,
    GET_STUDENT_INVOICES_BALANCE,
    GET_STUDENT_BADGES,
    GET_BLOGS,
    GET_BLOG,
  ],
  baseQuery: baseQueryWithInterceptor,
  endpoints: () => ({}),
});
